import { $thm } from '~/common/themeSx'
import { Container, RegionErrorBoundary } from '../..'
import { ArrowLeft } from '../../../icons'
import {
  Box,
  BoxProps,
  Flex,
  Heading,
  HeadingProps,
  IconButton,
  IconButtonProps,
  SimpleGrid,
  SimpleGridProps,
  Stack,
  StackProps,
  forwardRef
} from '../../chakra'

export type PageHeaderProps = {
  cardProps?: BoxProps & { 'data-testid'?: string }
} & StackProps

/**
 * Page Header
 *
 * This is the page header wrapper component. It will layout the header within our DS Container component
 * to ensure it is centered with the main content on the page. Please ensure your content is also wrapped
 * within the same Container component for exact alignment.
 *
 * This component includes the breadcrumb path based on the names and href provided. Please check Breadcrumbs
 * documentation in VNDLY's storybook.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-page-header--docs
 *
 * @agent-gotcha Default pb={6} below last child. When NavTabs is the last child (production tabbed pages), set pb={0} so tabs sit flush with the body — pattern from vendor_profile/Header.tsx.
 * @agent-gotcha NavTabs handles its own -1px border overlap inside data-page-header.
 */
export const PageHeader = forwardRef(({ children, maxW, cardProps, ...props }: PageHeaderProps, ref) => {
  const { sx: cardSx, ...restCardProps } = cardProps ?? {}
  return (
    <Box
      as="header"
      rounded={0}
      border="none"
      ref={ref}
      data-page-header
      sx={{
        ...$thm({
          bg: { $base: 'white', $canvas: 'none' },
          borderBottom: {
            $base: '1px solid var(--cnvs-sys-color-border-strong)',
            $canvas: 'none'
          },
          boxShadow: {
            $base: '0px 2px 4px 0px rgb(0,0,0,0.08)',
            $canvas: 'none'
          },
          p: { $canvas: 0 },
          my: { $canvas: 6 }
        }),
        ...cardSx
      }}
      {...restCardProps}>
      <Container position="relative" maxW={maxW}>
        <Stack
          direction="column"
          pos="relative"
          gap={2}
          w="100%"
          sx={$thm({
            pt: { $base: 4, $canvas: 0 },
            pb: { $base: 6, $canvas: 0 },
            '&:has([data-nav-tab-list])': {
              pb: 0
            }
          })}
          {...props}>
          <RegionErrorBoundary>{children}</RegionErrorBoundary>
        </Stack>
      </Container>
    </Box>
  )
})

PageHeader.displayName = 'PageHeader'

/**
 * Page Title
 *
 * This is the page title component, using Chakra's Heading component. This component is only used when
 * a title block is not needed.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-page-header--docs
 */
export const PageTitle = forwardRef(({ children, ...props }: HeadingProps, ref) => {
  return (
    <Heading as="h1" textStyle="heading1" ref={ref} data-testid="page-header-title" {...props}>
      {children ?? ''}
    </Heading>
  )
})

PageTitle.displayName = 'PageTitle'

/**
 * Page Back Button
 *
 * On secondary or tertiary pages (such as detail views), a back button may be needed to easily
 * allow the user to navigate back to the previous page. This component will automatically position
 * the back button in the correct place within the header. If the onClick function is not provided
 * it will use window.history.back() to go back. An aria-label tag is required to be passed when
 * using this component.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-page-header--docs
 */
export const PageBackButton = forwardRef(({ ...props }: IconButtonProps, ref) => (
  <IconButton size="md" isRound icon={<ArrowLeft />} onClick={() => window.history.back()} ref={ref} {...props} />
))

PageBackButton.displayName = 'PageBackButton'

/**
 * Page Actions Container
 *
 * Action buttons used on a page header will need to be wrapper within this component to ensure proper
 * spacing and alignment.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-page-header--docs
 */
export const PageActionsContainer = forwardRef((props: StackProps, ref) => (
  <Stack
    alignItems={{ base: 'flex-start', md: 'flex-end' }}
    spacing="sm"
    direction={{ base: 'column-reverse', md: 'column' }}
    ref={ref}
    {...props}
  />
))

PageActionsContainer.displayName = 'PageActionsContainer'

/**
 * Page Header Content Wrapper
 *
 * When there is a split view of content on both the left and right hand sides, this wrapper component can
 * be used to create a grid between the data.
 *
 * All page title info and action buttons should be wrapped with this component to achive the proper spacing
 * and alignment.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/patterns-page-header--docs
 */
export const PageHeaderWrapper = forwardRef(({ children, ...props }: SimpleGridProps, ref) => {
  return (
    <SimpleGrid
      templateColumns={{ base: '1fr', md: '2fr 1fr' }}
      spacing="sm"
      alignItems="flex-start"
      mt="auto"
      ref={ref}
      {...props}>
      {children}
    </SimpleGrid>
  )
})

PageHeaderWrapper.displayName = 'PageHeaderWrapper'

/**
 * @deprecated DO NOT USE (YET).  These are experimental components that
 * are not yet ready for production use (only used in SOW right now)
 */
export const ResponsivePageHeaderWrapper = forwardRef(({ children, ...props }: SimpleGridProps, ref) => {
  return (
    <Flex justifyContent="space-between" flexWrap="wrap" columnGap={8} rowGap={4} mt="auto" ref={ref} {...props}>
      {children}
    </Flex>
  )
})

ResponsivePageHeaderWrapper.displayName = 'ResponsivePageHeaderWrapper'
